import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, Button,
  Chip, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemIcon, ListItemText, Divider, Alert,
  CircularProgress, Tabs, Tab, LinearProgress, FormControl,
  InputLabel, Select, MenuItem, TextField, CardActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Badge, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Assessment, FileDownload, Print, DateRange, Star, CheckCircle,
  Warning, Error, Info, Visibility, Engineering, ElectricalServices,
  LocalFireDepartment, Plumbing, Assignment, TrendingUp, Schedule,
  LocationOn, Person, School, Verified, Certificate, FilterList,
  Search, GetApp, ExpandMore, PhotoCamera, Description, PictureAsPdf,
  Folder, CloudDownload, Analytics, BarChart, PieChart, Timeline
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import DialogCloseButton from '../components/common/DialogCloseButton';

const InspectorReportsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [reportDetailsDialog, setReportDetailsDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('current_month');
  
  // Data states
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [certifications, setCertifications] = useState([]);

  // Sample comprehensive reports data
  const sampleReportsData = {
    reports: [
      {
        id: 'RPT-001',
        inspectionId: 'INSP-001',
        projectTitle: 'Office Complex - Structural Assessment',
        inspectionType: 'structural',
        date: '2025-02-10',
        status: 'completed',
        client: 'Corporate Developments Ltd',
        location: 'Harare CBD',
        findings: 8,
        criticalIssues: 1,
        qualityScore: 98,
        timeSpent: 4.5,
        photosCount: 15,
        documentsCount: 3,
        followUpRequired: true,
        submittedDate: '2025-02-10',
        approvedDate: '2025-02-11'
      },
      {
        id: 'RPT-002',
        inspectionId: 'INSP-002',
        projectTitle: 'Shopping Mall - Electrical Compliance',
        inspectionType: 'electrical',
        date: '2025-02-08',
        status: 'under_review',
        client: 'Retail Development Corp',
        location: 'Bulawayo',
        findings: 12,
        criticalIssues: 0,
        qualityScore: 95,
        timeSpent: 6.0,
        photosCount: 22,
        documentsCount: 4,
        followUpRequired: false,
        submittedDate: '2025-02-08',
        approvedDate: null
      },
      {
        id: 'RPT-003',
        inspectionId: 'INSP-003',
        projectTitle: 'Residential Complex - Fire Safety',
        inspectionType: 'fire_safety',
        date: '2025-02-05',
        status: 'completed',
        client: 'Housing Development Co',
        location: 'Gweru',
        findings: 6,
        criticalIssues: 2,
        qualityScore: 92,
        timeSpent: 3.5,
        photosCount: 18,
        documentsCount: 2,
        followUpRequired: true,
        submittedDate: '2025-02-05',
        approvedDate: '2025-02-07'
      },
      {
        id: 'RPT-004',
        inspectionId: 'INSP-004',
        projectTitle: 'Industrial Warehouse - Structural',
        inspectionType: 'structural',
        date: '2025-02-03',
        status: 'draft',
        client: 'Industrial Holdings Ltd',
        location: 'Chitungwiza',
        findings: 0,
        criticalIssues: 0,
        qualityScore: null,
        timeSpent: 8.0,
        photosCount: 28,
        documentsCount: 1,
        followUpRequired: false,
        submittedDate: null,
        approvedDate: null
      }
    ],
    analytics: {
      totalReports: 156,
      completedReports: 142,
      avgQualityScore: 94.2,
      avgTimePerInspection: 5.2,
      totalFindings: 892,
      criticalIssues: 23,
      followUpsRequired: 45,
      clientSatisfactionRate: 96.8,
      onTimeDeliveryRate: 98.5,
      monthlyTrend: [
        { month: 'Sep', reports: 18, score: 93.2 },
        { month: 'Oct', reports: 22, score: 94.1 },
        { month: 'Nov', reports: 25, score: 95.0 },
        { month: 'Dec', reports: 20, score: 93.8 },
        { month: 'Jan', reports: 24, score: 94.7 },
        { month: 'Feb', reports: 16, score: 95.2 }
      ]
    },
    certifications: [
      {
        id: 'CERT-001',
        name: 'Structural Engineering Inspector',
        issuer: 'Zimbabwe Institute of Engineers',
        issueDate: '2023-01-15',
        expiryDate: '2026-01-15',
        status: 'active',
        credentialId: 'ZIE-SEI-2023-001',
        verificationUrl: 'https://zie.co.zw/verify/ZIE-SEI-2023-001'
      },
      {
        id: 'CERT-002',
        name: 'Fire Safety Inspector Level 2',
        issuer: 'Zimbabwe Fire Services',
        issueDate: '2023-06-20',
        expiryDate: '2025-06-20',
        status: 'expiring_soon',
        credentialId: 'ZFS-FSI2-2023-156',
        verificationUrl: 'https://fireservices.gov.zw/verify/ZFS-FSI2-2023-156'
      },
      {
        id: 'CERT-003',
        name: 'Electrical Systems Inspector',
        issuer: 'Electrical Contractors Association',
        issueDate: '2022-11-10',
        expiryDate: '2025-11-10',
        status: 'active',
        credentialId: 'ECA-ESI-2022-089',
        verificationUrl: 'https://eca.co.zw/verify/ECA-ESI-2022-089'
      }
    ]
  };

  // Load reports data from API, fallback to sample data
  useEffect(() => {
    const loadReportsData = async () => {
      try {
        setLoading(true);

        // Try real API first
        const [reportsRes, analyticsRes, certsRes] = await Promise.allSettled([
          api.get('/inspectors/reports/my'),
          api.get('/inspectors/reports/analytics'),
          api.get('/inspectors/certifications'),
        ]);

        if (reportsRes.status === 'fulfilled' && reportsRes.value.data?.reports) {
          setReports(reportsRes.value.data.reports);
        } else {
          console.warn('Inspector reports API unavailable, using sample data');
          setReports(sampleReportsData.reports);
        }

        if (analyticsRes.status === 'fulfilled' && analyticsRes.value.data?.analytics) {
          setAnalytics(analyticsRes.value.data.analytics);
        } else {
          setAnalytics(sampleReportsData.analytics);
        }

        if (certsRes.status === 'fulfilled' && certsRes.value.data?.certifications) {
          setCertifications(certsRes.value.data.certifications);
        } else {
          setCertifications(sampleReportsData.certifications);
        }

      } catch (err) {
        console.error('Error loading reports data:', err);
        // Fallback to sample data on complete failure
        setReports(sampleReportsData.reports);
        setAnalytics(sampleReportsData.analytics);
        setCertifications(sampleReportsData.certifications);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      loadReportsData();
    }
  }, [user?.id]);

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'success',
      'under_review': 'warning',
      'draft': 'info',
      'rejected': 'error'
    };
    return colors[status] || 'default';
  };

  const getCertificationStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'expiring_soon': 'warning',
      'expired': 'error'
    };
    return colors[status] || 'default';
  };

  const getInspectionTypeIcon = (type) => {
    const icons = {
      'structural': <Engineering />,
      'electrical': <ElectricalServices />,
      'fire_safety': <LocalFireDepartment />,
      'plumbing': <Plumbing />,
      'accessibility': <Assignment />,
      'general': <Assessment />
    };
    return icons[type] || <Assessment />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setReportDetailsDialog(true);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading reports data...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Inspection Reports & Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View your inspection reports, performance analytics, and certifications
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />}>
            Export Reports
          </Button>
          <Button variant="contained" startIcon={<Analytics />}>
            Generate Analytics
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary">
                {analytics.totalReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Reports
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Star sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {analytics.avgQualityScore}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Quality Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Schedule sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {analytics.avgTimePerInspection}h
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Time/Inspection
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {analytics.clientSatisfactionRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Client Satisfaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<Assessment />} label="Reports" />
          <Tab icon={<Analytics />} label="Performance Analytics" />
          <Tab icon={<Verified />} label="Certifications" />
          <Tab icon={<TrendingUp />} label="Quality Metrics" />
        </Tabs>
      </Box>

      {/* Reports Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Inspection Reports</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="structural">Structural</MenuItem>
                    <MenuItem value="electrical">Electrical</MenuItem>
                    <MenuItem value="fire_safety">Fire Safety</MenuItem>
                    <MenuItem value="plumbing">Plumbing</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Findings</TableCell>
                    <TableCell>Quality Score</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>{formatDate(report.date)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {report.projectTitle}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {report.client}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getInspectionTypeIcon(report.inspectionType)}
                          <Typography variant="body2">
                            {report.inspectionType.replace('_', ' ').toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            Total: {report.findings}
                          </Typography>
                          {report.criticalIssues > 0 && (
                            <Typography variant="caption" color="error">
                              Critical: {report.criticalIssues}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {report.qualityScore ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Star sx={{ color: 'gold', fontSize: 16 }} />
                            <Typography variant="body2">{report.qualityScore}%</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={report.status.replace('_', ' ').toUpperCase()} 
                          color={getStatusColor(report.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewReport(report)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Performance Analytics Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Monthly Performance Trend
                </Typography>
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Alert severity="info">
                    📊 Performance charts will be integrated here with a charting library like Chart.js or Recharts
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quality Score Distribution
                </Typography>
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Alert severity="info">
                    📈 Quality score distribution chart will be displayed here
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Certifications Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Professional Certifications
            </Typography>
            
            <Grid container spacing={3}>
              {certifications.map((cert) => (
                <Grid item xs={12} md={6} key={cert.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <School sx={{ color: 'primary.main' }} />
                          <Typography variant="h6">{cert.name}</Typography>
                        </Box>
                        <Chip 
                          label={cert.status.replace('_', ' ').toUpperCase()} 
                          color={getCertificationStatusColor(cert.status)}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Issued by: {cert.issuer}
                      </Typography>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Issue Date:</strong> {formatDate(cert.issueDate)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Expiry Date:</strong> {formatDate(cert.expiryDate)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Credential ID:</strong> {cert.credentialId}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                        <Button size="small" variant="outlined" startIcon={<Verified />}>
                          Verify
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<FileDownload />}>
                          Download
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            <Alert severity="warning" sx={{ mt: 3 }}>
              ⚠️ <strong>Renewal Required:</strong> Your Fire Safety Inspector Level 2 certification expires in 4 months. Please renew to maintain your qualification status.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Quality Metrics Tab */}
      {activeTab === 3 && (
        <Alert severity="info">
          📊 Quality metrics and detailed performance analytics interface will be built here with interactive charts and KPIs.
        </Alert>
      )}

      {/* Report Details Dialog */}
      <Dialog
        open={reportDetailsDialog}
        onClose={() => setReportDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogCloseButton onClick={() => setReportDetailsDialog(false)} />
        <DialogTitle>
          Inspection Report Details - {selectedReport?.id}
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Project Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Project Title"
                      secondary={selectedReport.projectTitle}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Project Owner"
                      secondary={selectedReport.client}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Location"
                      secondary={selectedReport.location}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Inspection Date"
                      secondary={formatDate(selectedReport.date)}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Report Summary
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Total Findings"
                      secondary={selectedReport.findings}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Critical Issues"
                      secondary={selectedReport.criticalIssues}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Quality Score"
                      secondary={selectedReport.qualityScore ? `${selectedReport.qualityScore}%` : 'Pending'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Time Spent"
                      secondary={`${selectedReport.timeSpent} hours`}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDetailsDialog(false)}>Close</Button>
          <Button variant="outlined" startIcon={<FileDownload />}>
            Download PDF
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print Report
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InspectorReportsPage; 